import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { CandyOffIcon, ServerCrashIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY,
  TABLE_ACCESSOR_STATUS,
  TicketList,
} from "~/components/common/tickets/ticket-list";
import { TicketsDisplay } from "~/components/common/tickets/tickets-display";
import { TicketsFilters } from "~/components/common/tickets/tickets-filters";
import { ViewForm } from "~/components/common/views/view-form";
import { APP_SIDEBAR_NAME } from "~/components/layout/sidebars/app-sidebar";
import { SidebarTrigger } from "~/components/ui/sidebar";
import {
  SupportTicketStatus,
  SupportTicketSubStatus,
  useViewQuery,
} from "~/graphql/graphql.gen";
import { useTickets } from "~/hooks/use-tickets";
import { countFilters, useQueueStore } from "~/stores/queue-store";
import {
  graphQLInputToQueryParams,
  queryParamsToFilters,
  queryParamsToTableColumnVisibility,
  queryParamsToTableFilters,
  queryParamsToTableGrouping,
  queryParamsToTableSorting,
  useTicketsStore,
} from "~/stores/tickets-store";
import { searchTicketsQueryParamsSchema } from "~/validators/search-tickets-validator";

export const DEFAULT_FILTERS: ColumnFiltersState = [
  {
    id: TABLE_ACCESSOR_STATUS,
    value: {
      operator: "is",
      value: [SupportTicketStatus.Todo, SupportTicketStatus.Snoozed],
    },
  },
];

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/view/$viewId",
)({
  component: RouteComponent,
  validateSearch: (search) => searchTicketsQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { viewId } = Route.useParams();

  const {
    error,
    data: view,
    isLoading,
  } = useViewQuery(
    {
      viewId: viewId,
    },
    {
      select: (data) => data.supportView,
    },
  );

  useEffect(() => {
    if (!view) return;
    void navigate({
      search: graphQLInputToQueryParams({
        displayedProperties: view.displayedProperties,
        filters: view.filters,
        groupBy: view.groupBy,
        sortBy: view.sortBy,
      }),
      replace: true,
    });
  }, [navigate, view]);

  const filterTickets = useTicketsStore((state) => state.filterTickets);
  const sortTickets = useTicketsStore((state) => state.sortTickets);
  const { data: tickets } = useTickets();
  const { setQueue } = useQueueStore();

  const filters = queryParamsToTableFilters(search.filters, DEFAULT_FILTERS);
  const columnVisibility = queryParamsToTableColumnVisibility(
    search,
    DEFAULT_COLUMN_VISIBILITY,
  );
  const grouping = queryParamsToTableGrouping(search);
  const sorting = queryParamsToTableSorting(search);

  useEffect(() => {
    setQueue({
      display: {
        filterCount: countFilters({
          currentFilters: filters,
          referenceFilters: DEFAULT_FILTERS,
        }),
        subTitle: t("ticket.status.todo"),
        title: t("app_sidebar.tickets"),
      },
      root: {
        page: {
          to: "/view/$viewId",
          params: {
            viewId: viewId,
          },
          search,
        },
      },
      state: {
        columnFilters: filters,
        sorting,
      },
    });
  }, [filters, search, setQueue, sorting, t, viewId]);

  if (isLoading)
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("view.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("view.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (!view || (error as Error | undefined)?.message === "view_not_found")
    return (
      <>
        <title>{`${t("view.not_found.title")} - ${t("app_name")}`}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full justify-center overflow-hidden">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("view.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("view.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("view.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  const ticketFilters = queryParamsToFilters(search.filters);
  const filteredTickets = sortTickets(filterTickets(tickets, filters), sorting);

  return (
    <>
      <title>{`${t("view.title", { name: view.title })} - ${t("app_name")}`}</title>

      <div className="bg-background flex w-full flex-col md:rounded-md md:border">
        <div className="flex h-full grow flex-col">
          <header className="flex flex-col items-center">
            <div className="flex h-10 w-full items-center gap-2 border-b px-4">
              <SidebarTrigger name={APP_SIDEBAR_NAME} className="-ml-1" />
              <Separator orientation="vertical" className="mr-2" />

              <ViewForm
                defaultValues={{
                  title: view.title,
                  icon: view.icon,
                  iconColor: view.iconColor,
                }}
                search={search}
                view={view}
              />
            </div>
            <div className="flex min-h-10 w-full items-center justify-between gap-2 border-b px-4 py-0.75 pl-5.5">
              <div className="flex flex-wrap items-center gap-2">
                <TicketsFilters
                  activeFilters={{
                    subStatus: [
                      SupportTicketSubStatus.NeedsFirstResponse,
                      SupportTicketSubStatus.NeedsNextResponse,
                      SupportTicketSubStatus.Investigating,
                      SupportTicketSubStatus.WaitingContact,
                      SupportTicketSubStatus.WaitingSupplier,
                      SupportTicketSubStatus.PausedForLater,
                    ],
                  }}
                  onFiltersChange={(filters) =>
                    void navigate({ search: (prev) => ({ ...prev, filters }) })
                  }
                  filters={ticketFilters}
                />
              </div>
              <TicketsDisplay
                onDisplayChange={(update) => {
                  void navigate({
                    search: (prev) => ({ ...prev, ...update(prev) }),
                  });
                }}
                display={search}
              />
            </div>
          </header>
          <div className="h-full w-full overflow-auto">
            <TicketList
              data={filteredTickets}
              hasFilters={Object.keys(ticketFilters).length > 0}
              state={{
                columnFilters: filters,
                columnVisibility: columnVisibility,
                grouping: grouping,
                sorting: sorting,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
